import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import type { RoleRecord } from '@/types/portal';

interface NewRoleModalProps {
  open: boolean;
  roles: RoleRecord[];
  onClose: () => void;
  onCreate: (name: string, description: string, cloneFromId: string) => void;
}

export default function NewRoleModal({ open, roles, onClose, onCreate }: NewRoleModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cloneFrom, setCloneFrom] = useState(roles[0]?.id ?? '');

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setCloneFrom(roles[0]?.id ?? '');
    }
  }, [open, roles]);

  const valid = name.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-shield-user-line"
      title="Create a role"
      subtitle="Clone an existing role as a starting point, then fine-tune permissions"
      widthClassName="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="ri-add-line"
            disabled={!valid}
            onClick={() => onCreate(name.trim(), description.trim(), cloneFrom)}
          >
            Create role
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="role-name"
            className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
          >
            Role name
          </label>
          <input
            id="role-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Campaign Coordinator"
            className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="role-desc"
            className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
          >
            Description
          </label>
          <textarea
            id="role-desc"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
            maxLength={160}
            placeholder="What is this role responsible for?"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="role-clone"
            className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
          >
            Clone permissions from
          </label>
          <div className="relative">
            <select
              id="role-clone"
              value={cloneFrom}
              onChange={(event) => setCloneFrom(event.target.value)}
              className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              <option value="">Start from scratch</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </div>
      </div>
    </Modal>
  );
}